/**
 * Purchase Detail Page
 *
 * Shows purchase header, line items, receive workflow and links to
 * generated inventory batches.
 *
 * @module pages/purchases/PurchaseDetail
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Dropdown,
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPurchase,
  cancelPurchase,
  receiveFullPurchase,
  resolveAttachmentUrl,
  PURCHASE_STATUS,
  PURCHASE_STATUS_LABELS,
  PURCHASE_STATUS_VARIANTS,
  isPurchaseEditable,
} from '../../services/purchaseService';
import {
  submitPOForApproval,
  approvePurchaseOrder,
  sendPOToSupplier,
  getContextualActions,
  getMyPurchaseApprovalScope,
} from '../../services/purchaseOrderService';
import { toast } from '../../utils/toast';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ReceivePurchaseModal from '../../components/purchases/ReceivePurchaseModal';
import ReceivePOModal from '../../components/purchases/ReceivePOModal';
import AttachInvoiceModal from '../../components/purchases/AttachInvoiceModal';
import RejectPOModal from '../../components/purchases/RejectPOModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';

const ITEM_STATUS_LABELS = {
  pending: 'Pendiente',
  partial: 'Parcial',
  received: 'Recibido',
  cancelled: 'Cancelado',
};

const ITEM_STATUS_VARIANTS = {
  pending: 'secondary',
  partial: 'warning',
  received: 'success',
  cancelled: 'danger',
};

const PurchaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReceiveAllModal, setShowReceiveAllModal] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);

  // Flow B (formal PO workflow — #130) state
  const [showReceivePOModal, setShowReceivePOModal] = useState(false);
  const [showAttachInvoiceModal, setShowAttachInvoiceModal] = useState(false);
  const [showRejectPOModal, setShowRejectPOModal] = useState(false);
  const [poActionPending, setPoActionPending] = useState(null); // 'submit'|'approve'|'send'

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => getPurchase(id),
    enabled: !!id,
  });

  // #550 — "solo los admin pueden aprobar cosas". Esto era la compuerta FINA
  // del esquema de delegación por proyecto
  // (`project_leaders.can_approve_purchases`). Retirada la delegación,
  // `getMyPurchaseApprovalScope` devuelve canApproveAny solo para el admin,
  // así que esta consulta ya no distingue por proyecto — se conserva la
  // llamada (y no se cablea `isAdmin` acá) para que la regla siga viviendo en
  // UN solo lugar del carril web. Quién puede escribir lo sigue decidiendo la
  // base (`can_approve_purchase` + el trigger); esto sólo decide qué se pinta.
  const { data: scope, isLoading: loadingScope } = useQuery({
    queryKey: ['purchase-approval-scope'],
    queryFn: getMyPurchaseApprovalScope,
    staleTime: 60_000,
  });

  const purchase = data?.data;
  const items = useMemo(() => purchase?.items || [], [purchase]);

  // El comprobante se guarda como PATH; la URL se firma fresca en cada
  // lectura y NUNCA se persiste. Antes se pintaba `attachment_url` crudo
  // como href, que era una signed URL de 1 hora: pasada esa hora, "Ver
  // factura" llevaba a un enlace muerto aunque el archivo siguiera en el
  // bucket. Mismo bug que #332 cerró en progress_photos.
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!purchase?.attachment_url) {
      setAttachmentUrl(null);
      return undefined;
    }
    resolveAttachmentUrl(purchase.attachment_url).then((url) => {
      if (!cancelled) setAttachmentUrl(url);
    });
    return () => { cancelled = true; };
  }, [purchase?.attachment_url]);

  const cancelMutation = useMutation({
    mutationFn: () => cancelPurchase(id),
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al cancelar: ' + (res.error.message || ''));
        return;
      }
      toast.success('Compra cancelada');
      queryClient.invalidateQueries({ queryKey: ['purchase', id] });
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      setShowCancelModal(false);
    },
    onError: (err) => toast.error('Error al cancelar: ' + err.message),
  });

  const receiveAllMutation = useMutation({
    mutationFn: () => receiveFullPurchase(id),
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al recibir: ' + (res.error.message || ''));
        return;
      }
      const failed = (res.data || []).filter((r) => r.error);
      if (failed.length > 0) {
        toast.warning(`Algunas lineas fallaron (${failed.length}). Revisa los detalles.`);
      } else {
        toast.success('Compra recibida en su totalidad');
      }
      queryClient.invalidateQueries({ queryKey: ['purchase', id] });
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      setShowReceiveAllModal(false);
    },
    onError: (err) => toast.error('Error al recibir compra: ' + err.message),
  });

  // ---- PO (Flow B) action handlers ----
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['purchase', id] });
    queryClient.invalidateQueries({ queryKey: ['purchases'] });
    queryClient.invalidateQueries({ queryKey: ['po-inbox'] });
  };

  const handlePOAction = async (action) => {
    if (!id) return;
    setPoActionPending(action);
    let res;
    if (action === 'submit') res = await submitPOForApproval(id);
    else if (action === 'approve') res = await approvePurchaseOrder(id);
    else if (action === 'send') res = await sendPOToSupplier(id);
    setPoActionPending(null);
    if (res?.error) {
      toast.error('Acción fallida: ' + res.error.message);
      return;
    }
    const label = {
      submit: 'PO enviada para aprobación',
      approve: 'PO aprobada',
      send: 'PO marcada como enviada al proveedor',
    }[action];
    toast.success(label);
    invalidateAll();
  };

  if (isLoading || loadingScope) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Cargando compra...</p>
      </Container>
    );
  }

  if (error || !purchase) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error?.message || 'No se pudo cargar la compra'}
          <Button variant="link" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/compras')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver
        </Button>
      </Container>
    );
  }

  // admin-only: purchase detail exposes supplier costs
  // Un líder con la aprobación delegada EN ESTE proyecto puede ver la compra
  // que le toca aprobar (la RLS `purchases_delegated_leader_read` ya se la
  // deja leer); cualquier otro no-admin, no.
  const isDelegatedLeaderHere =
    !!purchase.project_id && (scope?.projectIds || []).includes(purchase.project_id);
  const canViewPurchase = isAdmin || isDelegatedLeaderHere;

  if (!canViewPurchase) {
    return (
      <Container fluid className="py-5 text-center">
        <Alert variant="warning">
          <i className="bi bi-lock me-2"></i>
          No tienes permiso para ver el detalle de esta compra. Solo la ven los
          administradores y los líderes con la aprobación de compras delegada
          en su proyecto.
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </Container>
    );
  }

  // Sin `|| 'CRC'` (#617): la moneda sale de `purchases.currency` o no se rotula.
  const currency = purchase.currency;
  // Flow A: la compra nace 'invoiced'. Esto comparaba contra 'pending', valor
  // que el CHECK de `purchases` no admite — ninguna fila podía tenerlo, así
  // que las acciones de Flow A (Recibir Todo / Editar / Cancelar) estaban
  // muertas en pantalla.
  const isPending = purchase.status === PURCHASE_STATUS.INVOICED;
  const isCancelled = purchase.status === PURCHASE_STATUS.CANCELLED;
  // Editar cubre también el borrador de Flujo B — ver EDITABLE_PURCHASE_STATUSES.
  const isEditable = isPurchaseEditable(purchase.status);
  const hasReceivable =
    !isCancelled &&
    items.some(
      (it) =>
        it.status !== 'cancelled' &&
        Number(it.quantity_ordered || 0) > Number(it.quantity_received || 0)
    );

  const openReceive = (item) => {
    setReceiveItem(item);
    setShowReceiveModal(true);
  };

  const handleReceived = () => {
    queryClient.invalidateQueries({ queryKey: ['purchase', id] });
    queryClient.invalidateQueries({ queryKey: ['purchases'] });
  };

  return (
    <DashboardLayout>
    <Container fluid className="purchase-detail">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2 text-decoration-none"
            onClick={() => navigate('/compras')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Volver a compras
          </Button>
          <h2 className="mb-1">
            <i className="bi bi-receipt me-2"></i>
            {purchase.invoice_number || 'Compra (sin numero)'}
          </h2>
          <div className="text-muted">
            <Badge bg={PURCHASE_STATUS_VARIANTS[purchase.status]} className="me-2">
              {PURCHASE_STATUS_LABELS[purchase.status]}
            </Badge>
            {formatDate(purchase.invoice_date)}
            {purchase.supplier?.name && <> | {purchase.supplier.name}</>}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap" data-testid="po-action-bar">
          {/* Flow B — formal PO workflow contextual actions (#130).
              Al líder delegado se le muestran SOLO aprobar/rechazar: es lo
              único que la base le deja escribir
              (`purchases_delegated_leader_approve` exige
              status='pending_approval' y sólo permite pasar a
              approved/cancelled), y `receive_purchase` en el carril MCP es
              `ensureAdmin`. Pintarle "Recibir mercadería" sería ofrecerle un
              botón que sólo puede devolver un 403. */}
          {getContextualActions(purchase.status)
            .filter((action) => isAdmin || ['approve', 'reject'].includes(action.key))
            .map((action) => {
            const handler = () => {
              if (action.key === 'submit' || action.key === 'approve' || action.key === 'send') {
                handlePOAction(action.key);
              } else if (action.key === 'reject') {
                setShowRejectPOModal(true);
              } else if (action.key === 'receive') {
                setShowReceivePOModal(true);
              } else if (action.key === 'invoice') {
                setShowAttachInvoiceModal(true);
              }
            };
            const loading =
              (poActionPending === 'submit' && action.key === 'submit') ||
              (poActionPending === 'approve' && action.key === 'approve') ||
              (poActionPending === 'send' && action.key === 'send');
            return (
              <Button
                key={action.key}
                variant={action.variant}
                onClick={handler}
                disabled={!!poActionPending}
                data-testid={`po-action-${action.key}`}
              >
                {loading ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <i className={`bi ${action.icon} me-2`}></i>
                )}
                {action.label}
              </Button>
            );
          })}

          {/* Flow A legacy actions — admin-only: recibir, editar y cancelar
              una compra están fuera de la delegación por proyecto. */}
          {hasReceivable && isPending && isAdmin && (
            <Button
              variant="success"
              onClick={() => setShowReceiveAllModal(true)}
              disabled={receiveAllMutation.isPending}
            >
              <i className="bi bi-box-arrow-in-down me-2"></i>
              Recibir Todo
            </Button>
          )}
          {isEditable && isAdmin && (
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/compras/${id}/editar`)}
            >
              <i className="bi bi-pencil me-2"></i>
              Editar
            </Button>
          )}
          {isPending && !isCancelled && isAdmin && (
            <Button variant="outline-warning" onClick={() => setShowCancelModal(true)}>
              <i className="bi bi-x-circle me-2"></i>
              Cancelar Compra
            </Button>
          )}
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={12} lg={8}>
          {/* Header info */}
          <Card className="shadow-sm h-100">
            <Card.Header>
              <strong>Informacion general</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {purchase.project && (
                  <Col xs={12} data-testid="po-project">
                    <small className="text-muted d-block">Proyecto</small>
                    <Button
                      variant="link"
                      className="p-0 text-start text-decoration-none"
                      onClick={() => navigate(`/proyectos/${purchase.project.id}`)}
                    >
                      <strong>
                        {purchase.project.project_number
                          ? `${purchase.project.project_number} — ${purchase.project.title}`
                          : purchase.project.title}
                      </strong>
                    </Button>
                  </Col>
                )}
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Proveedor</small>
                  <strong>{purchase.supplier?.name || '-'}</strong>
                </Col>
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Fecha factura</small>
                  <strong>{formatDate(purchase.invoice_date)}</strong>
                </Col>
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Fecha recepcion</small>
                  <strong>{purchase.received_date ? formatDate(purchase.received_date) : '-'}</strong>
                </Col>
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Ubicacion</small>
                  <strong>{purchase.location || '-'}</strong>
                </Col>
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Moneda</small>
                  <strong>{currency}</strong>
                  {currency === 'USD' && (
                    <small className="ms-2 text-muted">TC: {purchase.exchange_rate}</small>
                  )}
                </Col>
                <Col xs={6} md={4}>
                  <small className="text-muted d-block">Comprobante</small>
                  {purchase.attachment_url ? (
                    <a
                      href={attachmentUrl || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-disabled={!attachmentUrl}
                      className={attachmentUrl ? undefined : 'text-muted'}
                    >
                      <i className="bi bi-file-earmark-text me-1"></i>
                      {attachmentUrl ? 'Ver comprobante' : 'Abriendo…'}
                    </a>
                  ) : (
                    <span className="text-danger">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      Falta
                    </span>
                  )}
                </Col>
                {purchase.notes && (
                  <Col xs={12}>
                    <small className="text-muted d-block">Notas</small>
                    <div>{purchase.notes}</div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          {/* Totals */}
          <Card className="shadow-sm h-100">
            <Card.Header>
              <strong>Totales</strong>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Subtotal</span>
                <span>{formatCurrency(purchase.subtotal, currency)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Impuesto</span>
                <span>{formatCurrency(purchase.tax_amount, currency)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <strong>Total</strong>
                <strong className="fs-5">
                  {formatCurrency(purchase.total, currency)}
                </strong>
              </div>
              {currency === 'USD' && Number(purchase.exchange_rate) > 0 && (
                <div className="mt-2 text-end small text-muted">
                  Equivalente: {formatCurrency(
                    Number(purchase.total) * Number(purchase.exchange_rate),
                    'CRC'
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Line items */}
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Lineas de la compra ({items.length})</strong>
        </Card.Header>
        <Card.Body className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox display-6"></i>
              <p className="mt-2 mb-0">No hay lineas registradas en esta compra</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Producto</th>
                    <th className="text-end">Ordenada</th>
                    <th className="text-end">Recibida</th>
                    <th className="text-end">Costo unit.</th>
                    <th className="text-end">Total</th>
                    <th>Lote</th>
                    <th>Vence</th>
                    <th>Estado</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const ordered = Number(item.quantity_ordered || 0);
                    const received = Number(item.quantity_received || 0);
                    const remaining = Math.max(0, ordered - received);
                    const canReceive =
                      !isCancelled &&
                      item.status !== 'cancelled' &&
                      remaining > 0;

                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">
                            {item.inventory?.sku ? `${item.inventory.sku} - ` : ''}
                            {item.inventory?.name || item.description || '—'}
                          </div>
                          {item.inventory?.unit && (
                            <small className="text-muted">{item.inventory.unit}</small>
                          )}
                          {item.notes && (
                            <small className="d-block text-muted">{item.notes}</small>
                          )}
                        </td>
                        <td className="text-end">{ordered}</td>
                        <td className="text-end">
                          {received}
                          {remaining > 0 && (
                            <small className="d-block text-warning">
                              (Pend. {remaining})
                            </small>
                          )}
                        </td>
                        <td className="text-end">
                          {formatCurrency(item.unit_cost, currency)}
                        </td>
                        <td className="text-end fw-semibold">
                          {formatCurrency(item.line_total, currency)}
                        </td>
                        <td>{item.lot_number || '-'}</td>
                        <td>
                          {item.expiration_date ? formatDate(item.expiration_date) : '-'}
                        </td>
                        <td>
                          <Badge bg={ITEM_STATUS_VARIANTS[item.status] || 'secondary'}>
                            {ITEM_STATUS_LABELS[item.status] || item.status}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <KebabMenu
                            label={`Acciones de ${item.inventory?.name || item.description || 'la línea'}`}
                            stopPropagation={false}
                          >
                              {canReceive && (
                                <Dropdown.Item onClick={() => openReceive(item)}>
                                  <i className="bi bi-box-arrow-in-down me-2"></i>
                                  Recibir
                                </Dropdown.Item>
                              )}
                              {item.batch_id && item.inventory_id && (
                                <Dropdown.Item
                                  onClick={() =>
                                    navigate(`/inventario/${item.inventory_id}/lotes`)
                                  }
                                >
                                  <i className="bi bi-box-seam me-2"></i>
                                  Ver lote generado
                                </Dropdown.Item>
                              )}
                              {!canReceive && !item.batch_id && (
                                <Dropdown.Item disabled>
                                  Sin acciones disponibles
                                </Dropdown.Item>
                              )}
                          </KebabMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Receive single item modal (Flow A) */}
      <ReceivePurchaseModal
        show={showReceiveModal}
        onHide={() => setShowReceiveModal(false)}
        item={receiveItem}
        currency={currency}
        onReceived={handleReceived}
      />

      {/* Flow B — receive whole PO */}
      <ReceivePOModal
        show={showReceivePOModal}
        onHide={() => setShowReceivePOModal(false)}
        purchase={purchase}
        onReceived={invalidateAll}
      />

      {/* Flow B — attach invoice to close PO */}
      <AttachInvoiceModal
        show={showAttachInvoiceModal}
        onHide={() => setShowAttachInvoiceModal(false)}
        purchase={purchase}
        onAttached={invalidateAll}
      />

      {/* Flow B — reject/cancel PO with reason */}
      <RejectPOModal
        show={showRejectPOModal}
        onHide={() => setShowRejectPOModal(false)}
        purchase={purchase}
        onRejected={invalidateAll}
      />

      {/* Receive all confirmation */}
      <Modal
        show={showReceiveAllModal}
        onHide={() => setShowReceiveAllModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-box-arrow-in-down text-success me-2"></i>
            Recibir compra completa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Esto recibira todas las lineas pendientes con sus cantidades ordenadas y
            generara los lotes correspondientes en inventario.
          </p>
          <Alert variant="info" className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Si necesitas recibir cantidades parciales o cambiar lote/vencimiento,
            usa la accion individual por linea.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReceiveAllModal(false)}
            disabled={receiveAllMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => receiveAllMutation.mutate()}
            disabled={receiveAllMutation.isPending}
          >
            {receiveAllMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Recibiendo...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Confirmar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel confirmation */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            Confirmar cancelacion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Estas seguro de cancelar esta compra? Los lotes ya generados se
            mantendran en inventario.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={cancelMutation.isPending}
          >
            Volver
          </Button>
          <Button
            variant="warning"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelando...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-2"></i>
                Cancelar compra
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DashboardLayout>
  );
};

export default PurchaseDetail;
